"use client";

/**
 * WebSocket Subscription System - React Hooks
 *
 * This module provides React hooks for subscribing to specific WebSocket alert types.
 * The subscription system replaces the old broadcast-all pattern, requiring explicit
 * subscriptions to improve performance and reduce unnecessary re-renders.
 *
 * Key Features:
 * - Explicit subscription to specific AlertTypes only
 * - Automatic subscription management (subscribe on mount, unsubscribe on unmount)
 * - Connection status tracking
 * - Support for conditional subscriptions
 * - Component name tracking for debugging
 */
import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocket } from "@/contexts/websocket-provider";
import createId from "@/lib/create-id";
import { AlertDetail } from "@/types/alert";
import { AlertType } from "@/types/enums/alert-type";

// Helper to get all alert types for components that need everything (e.g., alerts table)
export const ALL_ALERT_TYPES = Object.values(AlertType);

/**
 * Hook for subscribing to specific WebSocket alert types.
 *
 * This hook manages a single subscription to one or more alert types. It automatically
 * handles subscription lifecycle (subscribe on mount, unsubscribe on unmount) and
 * provides the latest message that matches the subscribed alert types.
 *
 * The hook uses stable references to prevent unnecessary re-subscriptions when
 * alert types don't actually change, and supports conditional subscriptions.
 *
 * `latestMessage` is a single state slot, so a burst of frames delivered in one
 * tick (the provider fans an array frame out as N synchronous notifies) collapses
 * to the last one. That is fine for consumers that refetch on any message, but
 * lossy for consumers that patch a cache per message — those should pass
 * `onMessage`, which fires once per frame.
 *
 * @param alertTypes - Array of AlertType enums to subscribe to
 * @param enabled - Whether the subscription is active (default: true)
 * @param componentName - Optional name for debugging and logging purposes
 * @param providerId - Optional provider scope for the subscription
 * @returns Object with latestMessage, isConnected, and isSubscribed status
 *
 * @example
 * ```typescript
 * // Basic usage
 * const { latestMessage, isConnected } = useWebSocketSubscription([
 *   AlertType.PAYMENT_SUCCEEDED,
 *   AlertType.PAYMENT_FAILED,
 * ]);
 *
 * // With conditional subscription and debugging
 * const { latestMessage } = useWebSocketSubscription(
 *   [AlertType.APPOINTMENT_SCHEDULED],
 *   userHasPermission,
 *   "AppointmentWidget"
 * );
 *
 * // Handle messages
 * useEffect(() => {
 *   if (latestMessage) {
 *     switch (latestMessage.type) {
 *       case AlertType.PAYMENT_SUCCEEDED:
 *         handlePaymentSuccess(latestMessage.data);
 *         break;
 *       case AlertType.PAYMENT_FAILED:
 *         handlePaymentFailure(latestMessage.data);
 *         break;
 *     }
 *   }
 * }, [latestMessage]);
 * ```
 */
function useWebSocketSubscriptionInternal(alertTypes: AlertType[], enabled: boolean, componentName?: string, providerId?: string | null, onMessage?: (message: AlertDetail) => void) {
  const {
    subscribe,
    isConnected
  } = useWebSocket();
  const [latestMessage, setLatestMessage] = useState<AlertDetail | null>(null);
  const onMessageRef = useRef(onMessage);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const subscriptionIdRef = useRef<string | null>(null);
  const prevSubscriptionKeyRef = useRef<string | null>(null);

  // Generate unique subscription ID on first render
  if (subscriptionIdRef.current === null) {
    subscriptionIdRef.current = createId("ws");
  }

  // Serialize alert types and providerId to prevent unnecessary re-subscriptions when array reference changes
  const subscriptionKey = JSON.stringify({
    alertTypes: [...alertTypes].sort(),
    providerId
  });

  // Written during commit so `handleMessage` can stay identity-stable — an inline
  // `onMessage` would otherwise re-run the subscribe effect on every render.
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);
  const handleMessage = useCallback((message: AlertDetail) => {
    if (onMessageRef.current) {
      // Callback-only: passing `onMessage` opts out of `latestMessage` entirely.
      // These subscriptions tend to live in always-mounted shells, where storing
      // a message nobody reads would rerender the whole subtree per frame.
      onMessageRef.current(message);
      return;
    }
    setLatestMessage(message);
  }, []);
  useEffect(() => {
    if (!enabled || !isConnected || alertTypes.length === 0) {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      prevSubscriptionKeyRef.current = null;
      return;
    }

    // Check if subscriptionKey changed - if so, unsubscribe first to resubscribe with new params
    const keyChanged = prevSubscriptionKeyRef.current !== subscriptionKey;
    if (keyChanged && unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    // Don't recreate subscription if we already have one for the same alert types and providerId
    if (unsubscribeRef.current) {
      return;
    }

    // Create new subscription
    unsubscribeRef.current = subscribe(alertTypes, handleMessage, subscriptionIdRef.current!, componentName, providerId);
    prevSubscriptionKeyRef.current = subscriptionKey;
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [subscriptionKey, enabled, isConnected, subscribe, handleMessage, componentName, providerId]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);
  return {
    latestMessage,
    isConnected,
    isSubscribed: !!unsubscribeRef.current
  };
}

/** Stateful subscription for consumers that render from the latest message. */
export function useWebSocketSubscription(alertTypes, t0, componentName, providerId) {
  const enabled = t0 === undefined ? true : t0;
  return useWebSocketSubscriptionInternal(alertTypes, enabled, componentName, providerId);
}

/**
 * `latestMessage` without the collapse: frames delivered together are handed
 * out one render at a time, so each is seen with the state the previous one
 * produced. For consumers whose effect also re-runs the latest message when
 * their own state changes, which a callback subscription would not do.
 */
export function useWebSocketQueuedSubscription(alertTypes, t0, componentName, providerId) {
  const $ = _c(10);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [queue, setQueue] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = message => setQueue(pending => [...pending, message]);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    isConnected,
    isSubscribed
  } = useWebSocketSubscriptionInternal(alertTypes, enabled, componentName, providerId, t2);
  let t3;
  if ($[2] !== queue.length) {
    t3 = () => {
      if (queue.length > 1) {
        setQueue(_temp);
      }
    };
    $[2] = queue.length;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== queue) {
    t4 = [queue];
    $[4] = queue;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useEffect(t3, t4);
  const t5 = queue[0] ?? null;
  let t6;
  if ($[6] !== isConnected || $[7] !== isSubscribed || $[8] !== t5) {
    t6 = {
      latestMessage: t5,
      isConnected,
      isSubscribed
    };
    $[6] = isConnected;
    $[7] = isSubscribed;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}

/** Callback subscription for consumers that must process every frame. */
function _temp(pending_0) {
  return pending_0.slice(1);
}
export function useWebSocketCallbackSubscription(alertTypes, onMessage, t0, componentName, providerId) {
  const $ = _c(3);
  const enabled = t0 === undefined ? true : t0;
  const {
    isConnected,
    isSubscribed
  } = useWebSocketSubscriptionInternal(alertTypes, enabled, componentName, providerId, onMessage);
  let t1;
  if ($[0] !== isConnected || $[1] !== isSubscribed) {
    t1 = {
      isConnected,
      isSubscribed
    };
    $[0] = isConnected;
    $[1] = isSubscribed;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}